import { useEffect, useRef, useState } from "react";
import { Row, Table, Container, Col, Form, Button, OverlayTrigger, Popover, InputGroup } from "react-bootstrap";
import { useMsal } from "@azure/msal-react";
import { useSelector } from "react-redux";
import SatAPI from "../../../_services/sat";
import toastService from "../../../_services/toastService";
import MessageModal from "../../_components/MessageModal";
import DocentList from "../olas/components/DocentList";
import AssignmentEdit from "./components/AssignmentEdit";
import { getFetchParamGenerator, getApiURL } from "../../../_services/sat";
import { downloadBlob } from "../../../_helpers/excel";
import { sortProgrammesLikeOpleidingenPage, isShownInOpleidingenFilter } from "../../../_helpers/sortProgrammes";

const api = SatAPI();
const TASK_ASSIGNMENT_DIFF_SNAPSHOT_STORAGE_KEY = "SAT-TaskAssignmentDiff-SnapshotName";
const TASK_ASSIGNMENT_DIFF_OPEN_STORAGE_KEY = "SAT-TaskAssignmentDiff-Open";

function TaskAssignment() {
  const { instance } = useMsal();
  const snapshot = useSelector(state => state.snapshot.fileName);

  const [tasks, setTasks] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingLight, setLoadingLight] = useState(false);
  const [labels, setLabels] = useState([]); // for Excel export
  const [diffSnapshotName, setDiffSnapshotName] = useState("");
  const [runningDiff, setRunningDiff] = useState(false);
  const [assignmentDiffData, setAssignmentDiffData] = useState({
    diff: null,
    currentTasks: [],
    snapshotTasks: [],
    currentAssignments: [],
    snapshotAssignments: [],
  });
  const [isDiffOpen, setIsDiffOpen] = useState(false);
  const [availableSnapshots, setAvailableSnapshots] = useState([]);

  const [modal, setModal] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [hasChanges, setHasChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [deletedTaskAssignments, setDeletedTaskAssignments] = useState([]);
  const diffDetailsContainerRef = useRef(null);
  const taskRowRefs = useRef({});
  const [highlightedTaskRowID, setHighlightedTaskRowID] = useState(null);

  // filters
  const [selectedProgrammeIDs, setSelectedProgrammeIDs] = useState([]);
  const [filterNotFull, setFilterNotFull] = useState(false);

  const markAsChanged = id => {
    setHasChanges({ ...hasChanges, [id]: true });
  };

  const calculateVTE = (task, programme) => {
    let vtePercentage = 0;
    task.assignments
      .filter(ta => ta.programmeID === programme.ID)
      .forEach(a => {
        if (a.percentageOverride > 0) {
          vtePercentage += a.percentageOverride;
          return;
        }
        vtePercentage += task.percentage;
      });

    return vtePercentage / 100;
  };

  const loadSnapshots = async () => {
    try {
      const snapshots = await api.getSnapshots();
      const snapshotNames = [...new Set(
        (snapshots || [])
          .map(s => (s.fileName || s.name || "").trim())
          .filter(Boolean),
      )].sort((a, b) => b.localeCompare(a));

      setAvailableSnapshots(snapshotNames);
      return snapshotNames;
    } catch {
      setAvailableSnapshots([]);
      return [];
    }
  };

  const snapshotExists = async snapshotName => {
    const snapshots = availableSnapshots.length > 0 ? availableSnapshots : await loadSnapshots();
    return snapshots.includes(snapshotName);
  };

  const formatApiError = async error => {
    if (error instanceof Response) {
      const body = await error.text();
      return body || `Request failed with status ${error.status}`;
    }

    return error?.message || String(error);
  };

  const setSelectedSnapshotName = snapshotName => {
    setDiffSnapshotName(snapshotName);
    localStorage.setItem(TASK_ASSIGNMENT_DIFF_SNAPSHOT_STORAGE_KEY, snapshotName);
  };

  const clearSelectedSnapshotName = () => {
    setDiffSnapshotName("");
    localStorage.removeItem(TASK_ASSIGNMENT_DIFF_SNAPSHOT_STORAGE_KEY);
  };

  const setDiffOpenState = isOpen => {
    setIsDiffOpen(isOpen);
    localStorage.setItem(TASK_ASSIGNMENT_DIFF_OPEN_STORAGE_KEY, isOpen ? "true" : "false");
  };

  const collapseAllDiffDetails = () => {
    const container = diffDetailsContainerRef.current;
    if (!container) {
      return;
    }

    container.querySelectorAll("details[open]").forEach(detail => detail.removeAttribute("open"));
  };

  const jumpToTaskRow = (taskID, programmeID) => {
    const rowID = `${taskID}-${programmeID}`;
    const row = taskRowRefs.current[rowID];

    if (!row) {
      toastService.send({ title: "Rij niet zichtbaar", message: "De overeenkomstige taakrij werd niet gevonden in de huidige tabelweergave." });
      return;
    }

    if (row.classList.contains("d-none")) {
      toastService.send({ title: "Rij gefilterd", message: "De rij is momenteel weggefilterd. Pas eerst de filters aan." });
      return;
    }

    row.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedTaskRowID(rowID);
    setTimeout(() => setHighlightedTaskRowID(null), 1800);
  };

  const openEditModalFromDiff = (taskAssignmentID, taskID, programmeID) => {
    const taskIndex = tasks.findIndex(task => `${task.ID}` === `${taskID}`);
    if (taskIndex === -1) {
      toastService.send({ title: "Taak niet gevonden", message: "De taak kon niet worden geopend vanuit de diff." });
      return;
    }

    const task = tasks[taskIndex];
    const assignmentIndex = task.assignments.findIndex(assignment => `${assignment.ID}` === `${taskAssignmentID}` || `${assignment.taskID}` === `${taskID}` && `${assignment.programmeID}` === `${programmeID}`);

    if (assignmentIndex === -1) {
      toastService.send({ title: "Toewijzing niet gevonden", message: "De bijhorende toewijzing is niet zichtbaar in de huidige tabelweergave." });
      return;
    }

    openEdit(taskIndex, assignmentIndex, task.assignments[assignmentIndex]);
  };

  const getTaskById = (taskID, sourceTasks) => {
    const normalizedTaskID = `${taskID}`;
    return (sourceTasks || []).find(task => `${task.ID ?? task.id}` === normalizedTaskID);
  };

  const getTaskNameById = (taskID, sourceTasks) => {
    const task = getTaskById(taskID, sourceTasks);
    return task?.name || `Taak ${taskID}`;
  };

  const getProgrammeNameById = (programmeID, sourceTasks) => {
    const normalizedProgrammeID = Number(programmeID);
    if (!Number.isFinite(normalizedProgrammeID)) {
      return `#${programmeID}`;
    }

    const programme = (sourceTasks || [])
      .flatMap(task => task?.programmes || [])
      .find(p => Number(p?.ID) === normalizedProgrammeID);

    return programme?.name || `#${normalizedProgrammeID}`;
  };

  const getCollegaName = colleague => {
    if (!colleague) {
      return "-";
    }

    const firstName = colleague.firstName || colleague.first_name || "";
    const lastName = colleague.lastName || colleague.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || colleague.name || colleague.collegaName || `#${colleague.ID ?? colleague.id ?? colleague.collegaID ?? colleague.collegaId}`;
  };

  const getCollegaNameById = (collegaID, assignmentSources) => {
    const normalizedCollegaID = Number(collegaID);
    if (!Number.isFinite(normalizedCollegaID)) {
      return `#${collegaID}`;
    }

    const assignment = (assignmentSources || []).find(item => Number(item?.collegaID) === normalizedCollegaID);
    return getCollegaName(assignment?.collega) || `#${normalizedCollegaID}`;
  };

  const getAssignmentValueDisplay = (fieldName, value, sourceTasks, assignmentSources) => {
    if (value === null || value === undefined || value === "") {
      return "(leeg)";
    }

    switch (fieldName) {
      case "collegaID":
        return getCollegaNameById(value, assignmentSources);
      case "programmeID":
        return getProgrammeNameById(value, sourceTasks);
      case "percentageOverride":
        return `${value}%`;
      default:
        return `${value}`;
    }
  };

  const normalizeAssignmentFieldName = rawFieldName => {
    const aliases = {
      collegaId: "collegaID",
      collegaID: "collegaID",
      collega_id: "collegaID",
      programmeId: "programmeID",
      programmeID: "programmeID",
      programme_id: "programmeID",
      percentage_override: "percentageOverride",
      percentageOverride: "percentageOverride",
      comment: "comment",
    };

    return aliases[rawFieldName] || rawFieldName;
  };

  const mapAssignmentFieldNameToLabel = fieldName => {
    const labelsByField = {
      collegaID: "Docent",
      programmeID: "Opleiding",
      percentageOverride: "Percentage aanpassing",
      comment: "Commentaar",
    };

    return labelsByField[normalizeAssignmentFieldName(fieldName)] || normalizeAssignmentFieldName(fieldName);
  };

  const normalizeComparisonValue = (fieldName, value) => {
    if (fieldName === "percentageOverride") {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : 0;
    }

    if (fieldName === "comment") {
      return value ?? "";
    }

    return value ?? null;
  };

  const getAssignmentCollegaDisplayName = (assignment, assignmentSources) => {
    if (!assignment) {
      return "-";
    }

    const assignmentId = assignment.taskAssignmentID ?? assignment.ID ?? assignment.id;
    if (assignmentId !== undefined && assignmentId !== null) {
      const matchingAssignment = (assignmentSources || []).find(item => `${item?.ID ?? item?.id ?? item?.taskAssignmentID}` === `${assignmentId}`);
      if (matchingAssignment) {
        return matchingAssignment.collegaName
          || getCollegaNameById(matchingAssignment.collegaID, assignmentSources)
          || getCollegaName(matchingAssignment.collega)
          || `#${matchingAssignment.collegaID}`;
      }
    }

    return assignment.collegaName
      || getCollegaNameById(assignment.collegaID, assignmentSources)
      || getCollegaName(assignment.collega)
      || `#${assignment.collegaID}`;
  };

  const buildAssignmentMovementItems = (diff, currentTaskSourceTasks, snapshotTaskSourceTasks, currentAssignmentSources, snapshotAssignmentSources) => {
    const created = (diff?.created || []).map(item => ({
      key: `created-${item.taskAssignmentID ?? item.taskID ?? item.programmeID}`,
      action: "aan taak toegewezen",
      taskID: item.taskID,
      taskName: item.taskName || getTaskNameById(item.taskID, currentTaskSourceTasks),
      programmeName: item.programmeName || getProgrammeNameById(item.programmeID, currentTaskSourceTasks),
      programmeID: item.programmeID,
      fromCollegaName: "-",
      toCollegaName: getAssignmentCollegaDisplayName(item, currentAssignmentSources),
      percentageOverride: item.percentageOverride ?? 0,
      comment: item.comment ?? "",
      taskAssignmentID: item.taskAssignmentID,
    }));

    const deleted = (diff?.deleted || []).map(item => ({
      key: `deleted-${item.taskAssignmentID ?? item.taskID ?? item.programmeID}`,
      action: "af taak gehaald",
      taskID: item.taskID,
      taskName: item.taskName || getTaskNameById(item.taskID, snapshotTaskSourceTasks),
      programmeName: item.programmeName || getProgrammeNameById(item.programmeID, snapshotTaskSourceTasks),
      programmeID: item.programmeID,
      fromCollegaName: getAssignmentCollegaDisplayName(item, snapshotAssignmentSources),
      toCollegaName: "-",
      percentageOverride: item.percentageOverride ?? 0,
      comment: item.comment ?? "",
      taskAssignmentID: item.taskAssignmentID,
    }));

    const updated = (diff?.updated || []).map(item => {
      const changedFields = [...new Set(item.fieldsChanged || [])].map(normalizeAssignmentFieldName);
      const colleagueChanged = changedFields.includes("collegaID");
      const percentageChanged = changedFields.includes("percentageOverride");
      const commentChanged = changedFields.includes("comment");
      const programmeChanged = changedFields.includes("programmeID");
      const changeLabels = [];

      if (colleagueChanged) {
        changeLabels.push("toewijzing veranderd");
      }
      if (percentageChanged) {
        changeLabels.push("percentage aangepast");
      }
      if (commentChanged) {
        changeLabels.push("comment aangepast");
      }
      if (programmeChanged) {
        changeLabels.push("opleiding aangepast");
      }

      const summaryAction = changeLabels.length > 0 ? changeLabels.join(", ") : "toewijzing veranderd";

      return {
        key: `updated-${item.taskAssignmentID ?? item.taskID ?? item.programmeID}`,
        action: summaryAction,
        taskID: item.taskID,
        taskName: getTaskNameById(item.taskID, currentTaskSourceTasks) || getTaskNameById(item.taskID, snapshotTaskSourceTasks),
        programmeName: getProgrammeNameById(item.programmeID, currentTaskSourceTasks) || getProgrammeNameById(item.programmeID, snapshotTaskSourceTasks),
        programmeID: item.programmeID,
        fromCollegaName: getAssignmentCollegaDisplayName({ taskAssignmentID: item.taskAssignmentID, collegaID: item.oldValues?.collegaID ?? item.collegaID }, snapshotAssignmentSources),
        toCollegaName: getAssignmentCollegaDisplayName({ taskAssignmentID: item.taskAssignmentID, collegaID: item.newValues?.collegaID ?? item.collegaID }, currentAssignmentSources),
        oldProgrammeName: getProgrammeNameById(item.oldValues?.programmeID ?? item.programmeID, snapshotTaskSourceTasks) || getProgrammeNameById(item.programmeID, snapshotTaskSourceTasks),
        newProgrammeName: getProgrammeNameById(item.newValues?.programmeID ?? item.programmeID, currentTaskSourceTasks) || getProgrammeNameById(item.programmeID, currentTaskSourceTasks),
        oldPercentageOverride: item.oldValues?.percentageOverride,
        newPercentageOverride: item.newValues?.percentageOverride,
        oldComment: item.oldValues?.comment,
        newComment: item.newValues?.comment,
        taskAssignmentID: item.taskAssignmentID,
      };
    });

    const flatSorted = [...updated, ...created, ...deleted].sort((a, b) => {
      const taskCompare = (a.taskName || "").localeCompare(b.taskName || "");
      if (taskCompare !== 0) {
        return taskCompare;
      }

      const order = { vervangen: 0, "aan taak toegewezen": 1, "af taak gehaald": 2 };
      return (order[a.action] ?? 99) - (order[b.action] ?? 99);
    });

    // Group movements by task
    const grouped = flatSorted.reduce((acc, item) => {
      const existing = acc.find(group => group.taskID === item.taskID);
      if (existing) {
        existing.movements.push(item);
      } else {
        acc.push({
          taskID: item.taskID,
          taskName: item.taskName,
          programmeName: item.programmeName,
          movements: [item],
        });
      }
      return acc;
    }, []);

    return grouped;
  };

  const getAssignmentTaskName = (assignment, taskSourceTasks) => {
    if (!assignment) {
      return "-";
    }

    return assignment.taskName
      || getTaskNameById(assignment.taskID, taskSourceTasks)
      || `Taak ${assignment.taskID}`;
  };

  const getAssignmentProgrammeName = (assignment, taskSourceTasks) => {
    if (!assignment) {
      return "-";
    }

    return assignment.programmeName
      || getProgrammeNameById(assignment.programmeID, taskSourceTasks)
      || `#${assignment.programmeID}`;
  };

  const getAssignmentCollegaName = assignment => {
    if (!assignment) {
      return "-";
    }

    return assignment.collegaName
      || getCollegaName(assignment.collega)
      || getCollegaNameById(assignment.collegaID, [assignment])
      || `#${assignment.collegaID}`;
  };

  const buildAssignmentDisplay = (assignment, taskSourceTasks) => ({
    taskName: getAssignmentTaskName(assignment, taskSourceTasks),
    programmeName: getAssignmentProgrammeName(assignment, taskSourceTasks),
    collegaName: getAssignmentCollegaName(assignment),
  });

  const buildLocalAssignmentDiff = (currentAssignments, snapshotAssignments, currentTaskSourceTasks, snapshotTaskSourceTasks) => {
    const currentLeft = [...(currentAssignments || [])];
    const snapshotLeft = [...(snapshotAssignments || [])];
    const matchedPairs = [];

    const takeMatch = (currentPredicate, snapshotPredicateFactory) => {
      const currentIndex = currentLeft.findIndex(currentPredicate);
      if (currentIndex === -1) {
        return false;
      }

      const currentItem = currentLeft[currentIndex];
      const snapshotIndex = snapshotLeft.findIndex(snapshotPredicateFactory(currentItem));
      if (snapshotIndex === -1) {
        return false;
      }

      const snapshotItem = snapshotLeft[snapshotIndex];
      currentLeft.splice(currentIndex, 1);
      snapshotLeft.splice(snapshotIndex, 1);
      matchedPairs.push([currentItem, snapshotItem]);
      return true;
    };

    while (takeMatch(
      currentItem => currentItem?.ID !== undefined && currentItem?.ID !== null,
      currentItem => snapshotItem => `${snapshotItem?.ID}` === `${currentItem?.ID}`,
    ));

    while (takeMatch(
      currentItem => currentItem?.taskID !== undefined && currentItem?.programmeID !== undefined,
      currentItem => snapshotItem => `${snapshotItem?.taskID}` === `${currentItem?.taskID}` && `${snapshotItem?.programmeID}` === `${currentItem?.programmeID}`,
    ));

    const created = currentLeft.map(assignment => {
      const display = buildAssignmentDisplay(assignment, currentTaskSourceTasks);
      return {
        taskAssignmentID: assignment.ID,
        taskID: assignment.taskID,
        taskName: display.taskName,
        programmeID: assignment.programmeID,
        programmeName: display.programmeName,
        collegaID: assignment.collegaID,
        collegaName: display.collegaName,
        percentageOverride: assignment.percentageOverride ?? 0,
        comment: assignment.comment ?? "",
      };
    });

    const deleted = snapshotLeft.map(assignment => {
      const display = buildAssignmentDisplay(assignment, snapshotTaskSourceTasks);
      return {
        taskAssignmentID: assignment.ID,
        taskID: assignment.taskID,
        taskName: display.taskName,
        programmeID: assignment.programmeID,
        programmeName: display.programmeName,
        collegaID: assignment.collegaID,
        collegaName: display.collegaName,
        comment: assignment.comment ?? "",
      };
    });

    const updated = matchedPairs.flatMap(([currentItem, snapshotItem]) => {
      const fieldsChanged = [];
      const oldValues = {};
      const newValues = {};
      const fieldsToCompare = ["collegaID", "programmeID", "percentageOverride", "comment"];

      fieldsToCompare.forEach(fieldName => {
        const currentValue = normalizeComparisonValue(fieldName, currentItem?.[fieldName]);
        const snapshotValue = normalizeComparisonValue(fieldName, snapshotItem?.[fieldName]);

        if (currentValue !== snapshotValue) {
          fieldsChanged.push(fieldName);
          oldValues[fieldName] = snapshotValue;
          newValues[fieldName] = currentValue;
        }
      });

      if (fieldsChanged.length === 0) {
        return [];
      }

      return [{
        taskAssignmentID: currentItem?.ID ?? snapshotItem?.ID,
        taskID: currentItem?.taskID ?? snapshotItem?.taskID,
        programmeID: currentItem?.programmeID ?? snapshotItem?.programmeID,
        fieldsChanged,
        oldValues,
        newValues,
      }];
    });

    return {
      created,
      updated,
      deleted,
      summary: {
        created: created.length,
        updated: updated.length,
        deleted: deleted.length,
      },
    };
  };

  const handleAuthFailure = response => {
    if (response.status === 401 || response.status === 403) {
      toastService.send({
        title: "Inloggen vereist",
        message: "Je sessie is verlopen of je hebt geen toegang meer. Log opnieuw in en probeer het nog eens.",
      });
      instance.loginRedirect({ ...loginRequest, prompt: "create" }).catch(() => {});
      return true;
    }

    return false;
  };

  const runDiffWithSnapshot = async (selectedSnapshot, showErrorToast = true, showLoadingIndicator = true) => {
    if (showLoadingIndicator) {
      setRunningDiff(true);
    }

    try {
      const [diffResult, currentTasksResponse, snapshotTasksResponse, currentAssignmentsResponse, snapshotAssignmentsResponse] = await Promise.allSettled([
        api.getTaskAssignmentsDiff(selectedSnapshot),
        api.getTasks(null),
        api.getTasks(selectedSnapshot),
        api.getTaskAssignments(null),
        api.getTaskAssignments(selectedSnapshot),
      ]);

      let result;

      const currentTasks = currentTasksResponse.status === "fulfilled" ? currentTasksResponse.value || [] : [];
      const snapshotTasks = snapshotTasksResponse.status === "fulfilled" ? snapshotTasksResponse.value || [] : [];
      const currentAssignments = currentAssignmentsResponse.status === "fulfilled" ? currentAssignmentsResponse.value || [] : [];
      const snapshotAssignments = snapshotAssignmentsResponse.status === "fulfilled" ? snapshotAssignmentsResponse.value || [] : [];

      if (diffResult.status !== "fulfilled") {
        const response = diffResult.reason;

        if (handleAuthFailure(response)) {
          return false;
        }

        if (response?.status === 404) {
          result = buildLocalAssignmentDiff(
            currentAssignments,
            snapshotAssignments,
            currentTasks,
            snapshotTasks,
          );
        } else {
          throw response;
        }
      } else {
  result = diffResult.value;
  // Enrich deleted/updated items with collega names from snapshot
  const snapshotAssignments = snapshotAssignmentsResponse.status === "fulfilled" ? snapshotAssignmentsResponse.value || [] : [];
  (result.updated || []).forEach(item => {
    if (!item.fromCollegaName) {
      const match = snapshotAssignments.find(a => `${a.ID}` === `${item.taskAssignmentID}`);
      if (match) {
        item.fromCollegaName = getCollegaName(match.collega);
      }
    }
  });
  (result.deleted || []).forEach(item => {
    if (!item.fromCollegaName) {
      const match = snapshotAssignments.find(a => `${a.ID}` === `${item.taskAssignmentID}`);
      if (match) {
        item.fromCollegaName = getCollegaName(match.collega);
      }
    }
  });
}

      setAssignmentDiffData({
        diff: result,
        currentTasks,
        snapshotTasks,
        currentAssignments,
        snapshotAssignments,
      });

      localStorage.setItem(TASK_ASSIGNMENT_DIFF_SNAPSHOT_STORAGE_KEY, selectedSnapshot);
      return true;
    } catch (error) {
      if (showErrorToast) {
        setAssignmentDiffData({
          diff: null,
          currentTasks: [],
          snapshotTasks: [],
          currentAssignments: [],
          snapshotAssignments: [],
        });
        toastService.send({ title: "Vergelijking mislukt", message: await formatApiError(error) });
      }
      return false;
    } finally {
      if (showLoadingIndicator) {
        setRunningDiff(false);
      }
    }
  };

  const runDiff = async () => {
    const selectedSnapshot = diffSnapshotName.trim();

    if (!selectedSnapshot) {
      toastService.send({ title: "Snapshot vereist", message: "Vul een snapshotnaam in." });
      return;
    }

    try {
      const exists = await snapshotExists(selectedSnapshot);
      if (!exists) {
        setAssignmentDiffData({
          diff: null,
          currentTasks: [],
          snapshotTasks: [],
          currentAssignments: [],
          snapshotAssignments: [],
        });
        setDiffOpenState(false);
        toastService.send({ title: "Snapshot bestaat niet", message: "De ingevulde snapshot bestaat niet." });
        return;
      }
    } catch (error) {
      toastService.send({ title: "Snapshotcontrole mislukt", message: error.toString() });
      return;
    }

    const succeeded = await runDiffWithSnapshot(selectedSnapshot, true, true);
    if (succeeded) {
      setDiffOpenState(true);
    }
  };

  const refreshDiffAfterDataChange = async () => {
    const selectedSnapshot = diffSnapshotName.trim();
    if (!selectedSnapshot || !assignmentDiffData.diff) {
      return;
    }

    await runDiffWithSnapshot(selectedSnapshot, false, false);
  };

  const restoreAssignmentFromSnapshot = async (updatedAssignmentItem) => {
    // Check if task still exists
    const task = getTaskById(updatedAssignmentItem.taskID, assignmentDiffData.currentTasks);
    if (!task) {
      toastService.send({
        title: "Taak niet meer beschikbaar",
        message: "Een toewijzing kan niet hersteld worden als de taak niet meer bestaat.",
      });
      return;
    }

    // Find the snapshot assignment with matching data
    const snapshotAssignment = (assignmentDiffData.snapshotAssignments || []).find(
      a => `${a.ID}` === `${updatedAssignmentItem.taskAssignmentID}` ||
           (`${a.taskID}` === `${updatedAssignmentItem.taskID}` && `${a.programmeID}` === `${updatedAssignmentItem.programmeID}`)
    );

    if (!snapshotAssignment) {
      toastService.send({
        title: "Snapshot-toewijzing niet gevonden",
        message: "De toewijzing kan niet in de snapshot worden gevonden.",
      });
      return;
    }

    const taskName = getTaskNameById(updatedAssignmentItem.taskID, assignmentDiffData.currentTasks);

    const executeRestore = async () => {
      setConfirmModal(null);
      try {
        // Build the restore payload from snapshot values
        const restorePayload = {
          ID: snapshotAssignment.ID,
          taskID: snapshotAssignment.taskID,
          programmeID: snapshotAssignment.programmeID,
          collegaID: snapshotAssignment.collegaID,
          percentageOverride: snapshotAssignment.percentageOverride ?? 0,
          comment: snapshotAssignment.comment ?? "",
        };

        await api.updateTaskAssignment(snapshotAssignment.ID, restorePayload);
        
        toastService.send({
          title: "Toewijzing hersteld",
          message: "De toewijzing is succesvol naar de snapshot hersteld.",
        });

        // Refresh the diff and main data
        await loadLight();
        await refreshDiffAfterDataChange();
      } catch (error) {
        toastService.send({
          title: "Herstel mislukt",
          message: error.toString(),
        });
      }
    };

    // Show confirmation modal
    setConfirmModal(
      <MessageModal
        title="Toewijzing herstellen"
        message={
          <>
            <span>Ben je zeker dat je deze toewijzing van <strong>{taskName}</strong> wil herstellen naar de snapshotversie?</span>
            <span className="d-flex gap-2 justify-content-end mt-3">
              <Button variant="secondary" onClick={() => setConfirmModal(null)}>Annuleren</Button>
              <Button variant="warning" onClick={executeRestore}>Herstellen</Button>
            </span>
          </>
        }
        icon="fas fa-rotate-left"
        hideModal={() => setConfirmModal(null)}
      />
    );
  };

  const load = async (setLoad = true) => {
    try {
      setLoading(setLoad);
      const response = await api.getProgrammes(snapshot);
      response.sort((a, b) => a.name.localeCompare(b.name));
      setProgrammes(response);

      const assignments = await api.getTaskAssignments(snapshot);
      const tasksResponse = await api.getTasks(snapshot);
      tasksResponse.sort((a, b) => a.name.localeCompare(b.name));

      tasksResponse.forEach(task => {
        task.assignments = assignments.filter(a => a.taskID === task.ID);
        task.assignments.sort((a, b) => (a?.collega?.lastName || "").localeCompare(b?.collega?.lastName || "") || (a?.collega?.firstName || "").localeCompare(b?.collega?.firstName || ""));
      });

      setTasks(tasksResponse);

      // check if SAT-PersonalProgrammeFilter exists in local storage - if not: create it to avoid null reference
      if (!localStorage.getItem('SAT-PersonalProgrammeFilter')) {
        localStorage.setItem('SAT-PersonalProgrammeFilter', JSON.stringify([]));
      }

      let personalProgrammeIDs = [];
      JSON.parse(localStorage.getItem('SAT-PersonalProgrammeFilter')).forEach(selectedProgramme => {
        personalProgrammeIDs = [...personalProgrammeIDs, selectedProgramme.ID];
      });
      setSelectedProgrammeIDs(personalProgrammeIDs);

      setLoading(false);
      return true;
    } catch (error) {
      toastService.send({ title: "An error occured", message: error.toString() });
      setLoading(false);
      return false;
    }
  };

  const loadLight = async (setLoad = true) => {
    setLoadingLight(setLoad);
    const assignments = await api.getTaskAssignments(snapshot);
    const tasksResponse = [...tasks];
    tasksResponse.sort((a, b) => a.name.localeCompare(b.name));

    tasksResponse.forEach(task => {
      task.assignments = assignments.filter(a => a.taskID === task.ID);
      task.assignments.sort((a, b) => (a?.collega?.lastName || "").localeCompare(b?.collega?.lastName || "") || (a?.collega?.firstName || "").localeCompare(b?.collega?.firstName || ""));
    });

    setTasks(tasksResponse);
    setLoadingLight(false);
  };

  useEffect(() => {
    // Restore persisted diff state and pre-load data for current snapshot context.
    const savedSnapshotName = localStorage.getItem(TASK_ASSIGNMENT_DIFF_SNAPSHOT_STORAGE_KEY) || "";
    const savedOpenState = localStorage.getItem(TASK_ASSIGNMENT_DIFF_OPEN_STORAGE_KEY);
    const shouldOpenDiff = savedOpenState === "true";

    setDiffSnapshotName(savedSnapshotName);
    setIsDiffOpen(shouldOpenDiff);

    const initializePage = async () => {
      setLoading(true);

      const [loaded] = await Promise.all([
        load(false),
        loadSnapshots(),
      ]);

      if (loaded && savedSnapshotName.trim()) {
        try {
          if (await snapshotExists(savedSnapshotName.trim())) {
            await runDiffWithSnapshot(savedSnapshotName.trim(), false, false);
          } else {
            clearSelectedSnapshotName();
            setDiffOpenState(false);
          }
        } catch {
          // Keep the page usable even if diff initialization fails.
        }
      }

      setLoading(false);
    };

    initializePage();
  }, [snapshot]);

  const removeAssignment = async (taskIndex, assignmentIndex) => {
    await api.deleteTaskAssignment(tasks[taskIndex].assignments[assignmentIndex].ID);
    await loadLight();
    await refreshDiffAfterDataChange();
    
    /*
    if (tasks[taskIndex].assignments[assignmentIndex].ID) {
      setDeletedTaskAssignments([...deletedTaskAssignments, tasks[taskIndex].assignments[assignmentIndex]]);
    }

    const newTasks = [...tasks];
    newTasks[taskIndex].assignments.splice(assignmentIndex, 1);
    setTasks(newTasks);
    markAsChanged(newTasks[taskIndex].ID);
    */
  };

    const updateAssignment = async (taskIndex, assignmentIndex, data) => {
    const newTasks = [...tasks];
      const x = await api.updateTaskAssignment(data.ID, data);

      if (newTasks[taskIndex].assignments[assignmentIndex].collegaID !== x.collegaID) {
        newTasks[taskIndex].assignments[assignmentIndex] = x;
        setTasks(newTasks);
      } else {
        await loadLight();
      }

      await refreshDiffAfterDataChange();
  };

  const openEdit = (taskIndex, assignmentIndex, data) => {
    console.log(data);
    setModal(
      <AssignmentEdit
        hideModal={() => {
          setModal(null);
        }}
        data={data}
        saveData={d => updateAssignment(taskIndex, assignmentIndex, d)}
        removeAssignment={() => removeAssignment(taskIndex, assignmentIndex)}
      />,
    );
  };

  const toggleSelectedProgrammeFilter = (id, checked) => {
    if (checked) {
      setSelectedProgrammeIDs([...selectedProgrammeIDs, id]);
    } else {
      setSelectedProgrammeIDs(selectedProgrammeIDs.filter(i => i !== id));
    }
  };

  const save = () => {
    setSaving(true);
    try {
      Promise.all(
        tasks
          .filter(task => hasChanges[task.ID])
          .map(task =>
            task.assignments.map(async assignment => {
              assignment.taskID = task.ID;
              if (assignment.ID) {
                await api.updateTaskAssignment(assignment.ID, assignment);
              } else {
                await api.createTaskAssignment(assignment);
              }
            }),
          ),
          deletedTaskAssignments.map(async assignment => {
            await api.deleteTaskAssignment(assignment.ID);
          }),
      ).then(() => {
        setHasChanges({});
        setDeletedTaskAssignments([]);
        load(false);
        toastService.send({ title: "Saved", message: "Saved successfully" });
      }
        
      );
      
      //api.createSnapshot();
    } catch (error) {
      toastService.send({ title: "An error occured", message: error.toString() });
    }

    setSaving(false);
  };

  const saveExcel = async () => {
    try {
      const { fetchParamCreator, fetch } = getFetchParamGenerator();
      const opts = fetchParamCreator.getTaskAssignmentsExcel(snapshot, {});
      const resp = await fetch(getApiURL() + opts.url, opts.options);
      const file = await resp.blob();
      downloadBlob(file, 'taaktoewijzingen.xlsx');
    } catch (resp) {
      console.warn(resp);
    }
  }

  const currentTasksForDiff = assignmentDiffData.currentTasks;
  const snapshotTasksForDiff = assignmentDiffData.snapshotTasks;
  const currentAssignmentsForDiff = assignmentDiffData.currentAssignments;
  const snapshotAssignmentsForDiff = assignmentDiffData.snapshotAssignments;
  const assignmentMovementItems = buildAssignmentMovementItems(
    assignmentDiffData.diff,
    currentTasksForDiff,
    snapshotTasksForDiff,
    currentAssignmentsForDiff,
    snapshotAssignmentsForDiff,
  );

  if (loading) {
    return (
      <div className="text-center">
        <i className="fad fa-spinner-third fa-spin fa-5x" />
      </div>
    );
  }

  const popover = (o, programme) => (
    <Popover className="docent-list">
      <Popover.Header as="h3">Docent toekennen</Popover.Header>
      <Popover.Body>
        <DocentList
          programme={programme}
          taskId={o.ID}
          selectDocent={collega => {
            const currentTask = tasks.find(x => x.ID === o.ID);
            api.createTaskAssignment(
              {
                taskID: currentTask.ID,
                collega,
                collegaID: collega.ID,
                programme,
                programmeID: programme.ID,
                comment: "",
              }
            ).then(x => {
              setTasks(
                tasks.map(task => {
                  if (task.ID === o.ID) {
                    task.assignments.push(x);
                  }
                  return task;
                }),
              )
              refreshDiffAfterDataChange();
            });
            
          }}
          ola={null}
        />
      </Popover.Body>
    </Popover>
  );

  return (
    <Container>
      {loadingLight &&
        <div className="loading-light">
          <div className="spin-center">
          <i className="fad fa-spinner-third fa-spin fa-5x" />
          <p>Data aan het opslaan</p>
          </div>
        </div>
      }
      {modal}
      {confirmModal}
      <Row className="mb-2">
        <Col>
          <h1>Taken</h1>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col className="d-flex justify-content-end align-items-center gap-2 flex-nowrap">
          <InputGroup style={{ maxWidth: "380px" }}>
            <Form.Control
              type="text"
              list={diffSnapshotName ? "task-assignment-diff-snapshot-options" : undefined}
              value={diffSnapshotName}
              onChange={e => setSelectedSnapshotName(e.target.value)}
              placeholder="Zoek snapshot"
              autoComplete="off"
            />
            <datalist id="task-assignment-diff-snapshot-options">
              {availableSnapshots.map(snapshotName => (
                <option key={snapshotName} value={snapshotName} />
              ))}
            </datalist>
            <Form.Select
              aria-label="Selecteer snapshot"
              value={diffSnapshotName}
              onChange={async e => {
                const selectedValue = e.target.value;
                setSelectedSnapshotName(selectedValue);
                const succeeded = await runDiffWithSnapshot(selectedValue, true, true);
                if (succeeded) {
                  setDiffOpenState(true);
                }
              }}
              className="flex-grow-0"
              style={{ width: "44px", paddingInline: "0.4rem", backgroundPosition: "center", textIndent: "-9999px", overflow: "hidden" }}
            >
              {availableSnapshots.map(snapshotName => (
                <option key={snapshotName} value={snapshotName}>{snapshotName}</option>
              ))}
            </Form.Select>
          </InputGroup>
          <Button variant="primary" onClick={runDiff} disabled={runningDiff} style={{ whiteSpace: "nowrap" }}>
            {runningDiff ? (
              <>
                <i className="fad fa-spinner-third fa-spin" />
                &nbsp;Vergelijken...
              </>
            ) : (
              <>
                <i className="far fa-code-compare" />
                &nbsp;Vergelijk toewijzingen
              </>
            )}
          </Button>
          <Button variant="secondary" onClick={saveExcel} disabled={saving || snapshot}>
            <i className="far fa-file-export" />
            &nbsp;Exporteren
          </Button>
          <OverlayTrigger trigger="click" placement="left" rootClose overlay={
            <Popover>
              <Popover.Header as="h3">Legende</Popover.Header>
              <Popover.Body>
                <p>
                  <span className="text-info">
                    <i className="far fa-comment-dots" />
                  </span>{" "}
                  Opmerking aanwezig
                </p>
              </Popover.Body>
            </Popover>
          }>
            <Button variant="info">
              <i className="fas fa-question" />
            </Button>
          </OverlayTrigger>
          {/*<Button variant="success" onClick={save} disabled={saving || snapshot} className="fixedElement">
            <i className="far fa-save" />
            &nbsp;Opslaan
          </Button>*/}
        </Col>
      </Row>

      {assignmentDiffData.diff && isDiffOpen && (
        <Row className="mb-3">
          <Col>
            <div className="border rounded p-3 bg-light">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <h5 className="mb-0">Toewijzingsveranderingen</h5>
                <div className="d-flex gap-3">
                  <Button variant="link" className="p-0 text-decoration-none" onClick={collapseAllDiffDetails}>
                    Alles inklappen
                  </Button>
                  <Button variant="link" className="p-0 text-decoration-none" onClick={() => setDiffOpenState(false)}>
                    Sluiten
                  </Button>
                </div>
              </div>
              <p className="mb-2">
                <strong>Aangepast:</strong> {assignmentMovementItems.reduce((sum, group) => sum + group.movements.length, 0)}
              </p>
              <div ref={diffDetailsContainerRef}>
                {assignmentMovementItems.length > 0 ? (
                  <div className="mt-2">
                    {assignmentMovementItems.map((taskGroup) => (
                      <div key={`task-group-${taskGroup.taskID}`} className="mb-3 border rounded bg-light px-3 py-2">
                        <div className="fw-semibold mb-2">
                          {taskGroup.taskName}
                          {taskGroup.programmeName ? (
                            <span className="text-muted fw-normal ms-2">({taskGroup.programmeName})</span>
                          ) : null}
                        </div>
                        <ul className="mb-0 list-unstyled ms-3">
                          {taskGroup.movements.map((item, index) => (
                            <li key={item.key ?? `${taskGroup.taskID}-${index}`} className="mb-2">
                              <details className="border rounded bg-white px-2 py-1">
                                <summary className="fw-semibold">
                                  <span className="text-primary">{item.action}</span>
                                </summary>
                                <div className="mt-2 small">
                                  <div className="d-flex gap-3 mb-2">
                                    <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => jumpToTaskRow(item.taskID, item.programmeID)}>
                                      Ga naar rij
                                    </Button>
                                    {item.action !== "af taak gehaald" && (
                                      <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => openEditModalFromDiff(item.taskAssignmentID, item.taskID, item.programmeID)}>
                                        Open bewerken
                                      </Button>
                                    )}
                                  </div>
                                  {item.action === "aan taak toegewezen" || item.action === "af taak gehaald" ? (
                                    <>
                                      <div className="mb-2">
                                        <strong>Docent:</strong> {item.toCollegaName !== "-" ? item.toCollegaName : item.fromCollegaName}
                                      </div>
                                      <div className="mb-2">
                                        <strong>Percentage:</strong> {item.percentageOverride ?? 0}%
                                      </div>
                                      <div className="mb-2">
                                        <strong>Commentaar:</strong> {item.comment || "(leeg)"}
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="mb-2">
                                        <strong>Docent:</strong>{" "}
                                        {item.fromCollegaName === item.toCollegaName
                                          ? (item.toCollegaName || item.fromCollegaName || "-")
                                          : <>{item.fromCollegaName || "-"} <span className="text-muted">→</span> {item.toCollegaName || "-"}</>}
                                      </div>
                                      {item.oldProgrammeName !== item.newProgrammeName ? (
                                        <div className="mb-2">
                                          <strong>Opleiding:</strong> {item.oldProgrammeName} → {item.newProgrammeName}
                                        </div>
                                      ) : null}
                                      {item.oldPercentageOverride !== undefined || item.newPercentageOverride !== undefined ? (
                                        <div className="mb-2">
                                          <strong>Percentage:</strong> {item.oldPercentageOverride ?? "-"}% → {item.newPercentageOverride ?? "-"}%
                                        </div>
                                      ) : null}
                                      {item.oldComment !== undefined || item.newComment !== undefined ? (
                                        <div className="mb-2">
                                          <strong>Commentaar:</strong> {item.oldComment || "(leeg)"} → {item.newComment || "(leeg)"}
                                        </div>
                                      ) : null}
                                    </>
                                  )}
                                </div>
                              </details>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span>&nbsp;-</span>
                )}
              </div>
            </div>
          </Col>
        </Row>
      )}

      <Row>
        <Col md={2}>
          <h3>Filter</h3>
          <hr />
          <Button
            variant={filterNotFull ? "secondary" : "outline-dark"}
            className="w-100 mb-3"
            onClick={() => setFilterNotFull(!filterNotFull)}
          >
            Geen toewijzing
          </Button>
          <h4>Opleiding</h4>
          <ul className="list-group">
            {sortProgrammesLikeOpleidingenPage(programmes, isShownInOpleidingenFilter).map(programme => (
              <li key={programme.ID} className="list-group-item text-break">
                <Form.Check
                  inline
                  label={programme.name}
                  id={programme.ID}
                  type="checkbox"
                  checked={selectedProgrammeIDs.includes(programme.ID)}
                  onChange={e => toggleSelectedProgrammeFilter(programme.ID, e.target.checked)}
                />
              </li>
            ))}
          </ul>
        </Col>
        <Col md={10}>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Naam</th>
                <th>Opleiding</th>
                <th className="assigncol">Invulling</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task, taskIndex) =>
                task.programmes.map(programme => {
                  const rowKey = `${task.ID}-${programme.ID}`;
                  const isVisible = (selectedProgrammeIDs.includes(programme.ID) || selectedProgrammeIDs.length === 0) &&
                    (!filterNotFull || task.assignments.filter(a => a.programmeID === programme.ID).length === 0);
                  const rowClassName = `${isVisible ? "" : "d-none"} ${highlightedTaskRowID === rowKey ? "table-active" : ""}`.trim();

                  return (
                    <tr
                      key={rowKey}
                      ref={el => {
                        if (el) {
                          taskRowRefs.current[rowKey] = el;
                        }
                      }}
                      className={rowClassName}
                    >
                      <td>
                        {task.name}
                        <br />
                        <i className="fas fa-arrow-right" />
                        &nbsp;{(task.assignments.filter(x => x.programmeID === programme.ID).reduce((n, { vte }) => n + Math.round(vte * 2) / 2, 0) / 100).toFixed(3)}&nbsp;VTE
                      </td>
                      <td>{programme.name}</td>
                      <td>
                        <Row className="mx-2">
                          {task.assignments.map(
                            (ta, i) =>
                              ta.programmeID === programme.ID && (
                                // eslint-disable-next-line react/no-array-index-key
                                <Col xs={2} className="my-2 px-1" key={i}>
                                  <Button variant="outline-dark" className="collegaButton" onClick={() => openEdit(taskIndex, i, ta)}>
                                    <div className="collega-name text-truncate">
                                      {localStorage.getItem("SAT-typeName") === "volledig"
                                        ? ta.collega.firstName
                                        : ta.collega.initials}
                                    </div>

                                    {localStorage.getItem("SAT-typeName") === "volledig" && (
                                      <div className="collega-name text-truncate">{ta.collega.lastName}</div>
                                    )}
                                    <div className="collega-name text-truncate fw-bold">
                                      {ta.vte}%{" "}
                                      {(ta.comment || ta.commentTeacher) && (
                                        <span className="text-info">
                                          <i className="far fa-comment-dots" />
                                        </span>
                                      )}
                                    </div>
                                  </Button>
                                </Col>
                              ),
                          )}
                          <Col xs={2} className="my-2 px-1" key={task.assignments.length}>
                            <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={popover(task, programme)}>
                              <Button className="plusButton">
                                <i className="fas fa-plus" />
                              </Button>
                            </OverlayTrigger>
                          </Col>
                        </Row>
                      </td>
                    </tr>
                  );
                }),
              )}
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container >
  );
}

export default TaskAssignment;
